import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Calendar, Check, Phone, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { GuideStrip } from "../components/guide-strip";
import { HomeTrustBadges } from "../components/home-trust-badges";
import { openQuoteModal } from "../components/quote-modal";
import { HARDSCAPE_FACTS } from "../data/hardscape-facts";
import { paverCliftonParkHeroOpt } from "../assets/opt";
import { InlineQuoteSection } from "@/components/inline-quote-section";
import { TopQuoteForm } from "@/components/top-quote-form";

const SITE = "https://capitalpropressurewashing.com";
const URL = `${SITE}/hardscape-maintenance`;
const TITLE = "Hardscape Maintenance Program | Albany & Capital Region NY";
const DESC =
  "A yearly hardscape maintenance program for Capital Region patios, walkways & driveways: scheduled cleaning, joint top-ups, sealer refresh and priority booking.";

const PHONE_DISPLAY = "(518) 900-1913";
const PHONE_TEL = "tel:+15189001913";

const TIERS = [
  {
    name: "Maintain",
    tagline: "For hardscape we've already restored",
    points: [
      "One scheduled cleaning visit per year",
      "Joint inspection with photo report",
      "Spot weed & moss treatment",
      "Priority spring scheduling",
      "10% off any add-on service",
    ],
  },
  {
    name: "Protect",
    featured: true,
    tagline: "Our most popular hardscape plan",
    points: [
      "Everything in Maintain",
      "Spring clean + fall pre-winter rinse",
      "Polymeric joint top-ups as needed",
      "Sealer condition check each year",
      "Salt & efflorescence treatment",
      "15% off any add-on service",
    ],
  },
  {
    name: "Preserve",
    tagline: "Full lifecycle care, nothing to track",
    points: [
      "Everything in Protect",
      "Scheduled sealer refresh on cycle (3–5 yrs)",
      "Full joint re-sand when inspection calls for it",
      "Locked-in pricing for the term",
      "First-call priority for storm & event cleanups",
    ],
  },
];

const WHY = [
  { icon: Calendar, title: "Nothing to Remember", body: "We track your sealer age and joint condition and reach out when it's time — you never have to guess what year you last sealed." },
  { icon: ShieldCheck, title: "Catch Failure Early", body: "A washed-out joint is a $200 fix. A settled, shifted patio is a $5,000 rebuild. Annual inspection is the cheapest insurance your hardscape can have." },
  { icon: Sparkles, title: "It Always Looks New", body: "Maintained hardscape never gets to the 'green and grimy' stage, so every restoration visit is lighter, faster, and cheaper than the last." },
];

const FAQS = [
  { q: "Who is the hardscape maintenance program for?", a: "Homeowners with paver patios, walkways, driveways, pool decks, or sealed concrete who don't want to think about upkeep. It's the same crew, on a schedule, with your surfaces on file." },
  { q: "Do I need a full restoration before joining?", a: "Not always. If your hardscape is in good shape, we can start you on a maintenance tier right away. If joints have failed or the surface has never been sealed, we do a one-time restoration first, then maintain it." },
  { q: "What does the program cost?", a: "Pricing is based on total hardscape square footage and the tier you choose. We measure on site and give you a flat annual number in writing — no surprise invoices." },
  { q: "Can I cancel?", a: "Yes. There's no long-term lock-in on the Maintain and Protect tiers. Preserve runs on a term because it includes the scheduled sealer refresh." },
  { q: "Does this cover concrete as well as pavers?", a: "Yes — poured concrete drives and walks sealed with Trident Break Wall or Sea Wall are covered on the same schedule, including annual condition checks." },
  { q: "What areas do you cover?", a: "The full Capital Region — Albany, Schenectady, Troy, Saratoga Springs, Clifton Park, Colonie, Latham, Niskayuna, Guilderland, Delmar, Ballston Spa, Rensselaer and surrounding towns." },
];

export const Route = createFileRoute("/hardscape-maintenance")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { property: "og:image", content: `${SITE}${paverCliftonParkHeroOpt.url}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `${SITE}${paverCliftonParkHeroOpt.url}` },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Hardscape Maintenance Program",
          serviceType: "Paver and concrete hardscape maintenance",
          provider: { "@type": "LocalBusiness", name: "Capital Pro Pressure Washing", telephone: "+1-518-900-1913", url: SITE },
          areaServed: HARDSCAPE_FACTS.map((f) => ({ "@type": "City", name: `${f.city}, NY` })),
          url: URL,
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Hardscape Maintenance Tiers",
            itemListElement: TIERS.map((t) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: `${t.name} Hardscape Plan`, description: t.tagline },
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
    ],
  }),
  component: HardscapeMaintenancePage,
});

function HardscapeMaintenancePage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Hardscape Maintenance Program" }]} />

      <section className="relative bg-[var(--brand-deep-blue)] text-white overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #1E6FD9 0, transparent 45%), radial-gradient(circle at 80% 70%, #C8102E 0, transparent 45%)" }} />
        <div className="relative mx-auto max-w-[1200px] px-4 py-16 md:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/80 mb-3" style={{ fontFamily: "var(--font-display)" }}>
            Albany & the Capital Region · Yearly Care
          </p>
          <h1 className="text-4xl md:text-6xl uppercase leading-[1.05] tracking-tight max-w-[20ch]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
            Hardscape Maintenance Program
          </h1>
          <p className="mt-5 max-w-[62ch] text-white/85 text-lg">
            Pavers and concrete are an investment, not a one-time purchase. Our program keeps your patio, walkway, and driveway cleaned, re-sanded, and sealed on schedule — so you never pay for a rebuild you could have prevented.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => openQuoteModal({ plan: "Hardscape Maintenance Program", services: ["Paver Cleaning & Sealing"] })} className="inline-flex items-center gap-2 bg-[#C8102E] hover:brightness-90 text-white font-bold px-6 py-3 rounded-md transition">
              Get My Free Quote <ArrowRight size={18} />
            </button>
            <a href={PHONE_TEL} data-call-cta className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/25 text-white font-bold px-6 py-3 rounded-md transition">
              <Phone size={18} /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>
      <TopQuoteForm source="Hardscape Top" defaultService="pavers" heading="Free Hardscape Maintenance Quote" />

      <HomeTrustBadges />

      <section className="bg-white">
        <div className="mx-auto max-w-[1200px] px-4 py-16">
          <div className="grid gap-6 md:grid-cols-3">
            {WHY.map((w) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title}>
                  <div className="h-full rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-6">
                    <Icon className="text-[#C8102E]" size={26} />
                    <h2 className="mt-3 text-xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>{w.title}</h2>
                    <p className="mt-2 text-[var(--brand-charcoal)]/80">{w.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[var(--brand-light-gray)]">
        <div className="mx-auto max-w-[1200px] px-4 py-16">
          <Reveal>
            <h2 className="text-3xl md:text-4xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
              Choose Your Level of Care
            </h2>
            <p className="mt-3 max-w-[70ch] text-[var(--brand-charcoal)]/80">
              Every plan is priced off your actual hardscape square footage after an on-site measure. No two patios are the same, so we don't pretend otherwise.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {TIERS.map((t) => (
              <Reveal key={t.name}>
                <div className={`h-full rounded-lg bg-white p-6 border-2 ${t.featured ? "border-[#C8102E] shadow-lg" : "border-gray-200"}`}>
                  {t.featured && (
                    <span className="inline-block mb-3 text-[11px] font-bold uppercase tracking-wider bg-[#C8102E] text-white px-2 py-1 rounded">
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-2xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>{t.name}</h3>
                  <p className="mt-1 text-sm text-[var(--brand-charcoal)]/70">{t.tagline}</p>
                  <ul className="mt-4 space-y-2">
                    {t.points.map((p) => (
                      <li key={p} className="flex gap-2 text-[var(--brand-charcoal)]/85 text-sm">
                        <Check size={17} className="text-green-600 shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => openQuoteModal({ plan: `${t.name} Hardscape Plan`, services: ["Paver Cleaning & Sealing"] })} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[var(--brand-deep-blue)] hover:brightness-110 text-white font-bold px-5 py-3 rounded-md transition">
                    Get Pricing <ArrowRight size={17} />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1200px] px-4 py-16">
          <Reveal>
            <h2 className="text-3xl md:text-4xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
              Hardscape Care By Town
            </h2>
          </Reveal>
          <ul className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {HARDSCAPE_FACTS.map((f) => (
              <li key={f.slug}>
                <a href={`/paver-restoration-sealing-${f.slug}-ny`} className="block rounded-md border border-gray-200 px-4 py-3 hover:border-[var(--brand-deep-blue)] hover:bg-[var(--brand-light-gray)] transition text-[var(--brand-bright-blue)] font-semibold">
                  Paver restoration &amp; sealing in {f.city}, NY
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-[var(--brand-light-gray)]">
        <div className="mx-auto max-w-[900px] px-4 py-16">
          <h2 className="text-3xl md:text-4xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
            Program FAQs
          </h2>
          <div className="mt-8 space-y-4">
            {FAQS.map((f) => (
              <details key={f.q} className="rounded-lg bg-white border border-gray-200 p-5">
                <summary className="cursor-pointer font-bold text-[var(--brand-deep-blue)]">{f.q}</summary>
                <p className="mt-3 text-[var(--brand-charcoal)]/80">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--brand-deep-blue)] text-white">
        <div className="mx-auto max-w-[900px] px-4 py-16 text-center">
          <h2 className="text-3xl md:text-4xl uppercase" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
            Stop Rebuilding. Start Maintaining.
          </h2>
          <p className="mt-3 text-white/85">Free on-site measure and a flat annual price in writing.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button onClick={() => openQuoteModal({ plan: "Hardscape Maintenance Program", services: ["Paver Cleaning & Sealing"] })} className="inline-flex items-center gap-2 bg-[#C8102E] hover:brightness-90 text-white font-bold px-6 py-3 rounded-md transition">
              Get My Free Quote <ArrowRight size={18} />
            </button>
            <a href={PHONE_TEL} data-call-cta className="inline-flex items-center gap-2 bg-white text-[var(--brand-deep-blue)] font-bold px-6 py-3 rounded-md transition hover:bg-white/90">
              <Phone size={18} /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>
    <GuideStrip serviceSlug="paver-cleaning-sealing" serviceName="Hardscape Maintenance" />
      <InlineQuoteSection source="Hardscape Maintenance" defaultService="pavers" />
    </main>
  );
}
